import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Text,
  VStack,
  Badge,
  useToast,
  Input,
  FormControl,
  FormLabel,
  Flex,
  IconButton,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  SimpleGrid,
  Image,
} from "@chakra-ui/react";
import {
  FiUpload,
  FiFolder,
  FiImage,
  FiFilm,
  FiTrash2,
  FiClock,
} from "react-icons/fi";
import { useDispatch } from "react-redux";
import { format } from "date-fns";
import { hitBeApi, hitBeApiFormData } from "../../../api/api";
import { appStore } from "../../../store/Store";
import VoiceCreditPaymentModal from "../VoiceCreditPaymentModal";
import {
  useAiFeatureColors,
  AiCreditsBanner,
  AiCreditsPaywall,
  AiHistorySidebar,
  AiHistoryItem,
  AiFeatureGuidePanel,
  AiStepStrip,
} from "../ai/aiFeatureUi";
import { useAiCreditsGate } from "../../../hooks/useAiCreditsGate";
import { DASHBOARD_TAB_GUIDES } from "../../../content/productGuide";
import MediaAnalyzeItemCard from "./MediaAnalyzeItemCard";
import MediaAnalyzeVideoSources from "./MediaAnalyzeVideoSources";
import { revokeBlobUrl } from "../../../utils/mediaAnalyzeMedia";

const ACCEPT_FILES =
  "image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime,.jpg,.jpeg,.png,.webp,.gif,.mp4,.webm,.mov";

function formatJobDate(iso) {
  if (!iso) return "";
  try {
    return format(new Date(iso), "MMM d, yyyy · h:mm a");
  } catch {
    return "";
  }
}

function sourceTypeLabel(t) {
  if (t === "video") return "Video";
  if (t === "image") return "Image";
  if (t === "mixed") return "Mixed";
  return "Images";
}

function filePreviewKind(file) {
  const mime = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "");
  if (mime.startsWith("image/") || /\.(jpe?g|png|webp|gif|bmp)$/i.test(name)) {
    return "image";
  }
  if (mime.startsWith("video/") || /\.(mp4|webm|mov|m4v)$/i.test(name)) {
    return "video";
  }
  return "file";
}

export default function MediaAnalyze() {
  const toast = useToast();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const {
    remaining: aiCreditsRemaining,
    canUse: canAnalyze,
    payOpen: voicePayOpen,
    setPayOpen: setVoicePayOpen,
    promptBuy: promptBuyCredits,
  } = useAiCreditsGate();
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [activeJobId, setActiveJobId] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [title, setTitle] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [processing, setProcessing] = useState(false);

  const {
    pageBg,
    cardBg,
    borderColor,
    muted,
    dropBg,
    dropBorder,
    segmentBg,
    accent,
  } = useAiFeatureColors("purple");

  const applyCredits = useCallback(
    (userTokenDetails) => {
      if (userTokenDetails) {
        const currentUser = appStore.getState().user || {};
        dispatch({
          type: "SET_USER",
          value: { ...currentUser, userTokenDetails },
        });
      }
    },
    [dispatch]
  );

  const loadJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const res = await hitBeApi("dashboard/media-analyze/jobs", "GET");
      setJobs(res?.data?.jobs || []);
    } catch {
      setJobs([]);
    } finally {
      setJobsLoading(false);
    }
  }, []);

  const loadJob = useCallback(
    async (id) => {
      if (!id) {
        setActiveJob(null);
        return;
      }
      try {
        const res = await hitBeApi(`dashboard/media-analyze/jobs/${id}`, "GET");
        setActiveJob(res?.data?.job || null);
      } catch (e) {
        toast({
          title: "Could not load analysis",
          description: e.message,
          status: "error",
          duration: 4000,
        });
      }
    },
    [toast]
  );

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    if (activeJobId) loadJob(activeJobId);
  }, [activeJobId, loadJob]);

  const isAllowedFile = (f) => {
    const mime = String(f.type || "").toLowerCase();
    if (mime.startsWith("image/") || mime.startsWith("video/")) return true;
    return /\.(jpe?g|png|webp|gif|bmp|mp4|webm|mov|m4v)$/i.test(f.name || "");
  };

  const addFiles = (fileList) => {
    if (!canAnalyze) {
      promptBuyCredits();
      return;
    }
    const arr = Array.from(fileList || []).filter(isAllowedFile);
    if (!arr.length) {
      toast({
        title: "No supported files",
        description: "Choose images (JPEG, PNG, WebP) or video (MP4, WebM, MOV).",
        status: "info",
        duration: 5000,
      });
      return;
    }
    setPendingFiles((prev) => {
      const names = new Set(prev.map((f) => `${f.name}-${f.size}`));
      const merged = [...prev];
      for (const f of arr) {
        const key = `${f.name}-${f.size}`;
        if (!names.has(key)) {
          merged.push(f);
          names.add(key);
        }
      }
      return merged;
    });
  };

  const removePending = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const clearPending = () => setPendingFiles([]);

  const runAnalysis = async () => {
    if (!canAnalyze) {
      promptBuyCredits();
      return;
    }
    if (!pendingFiles.length) {
      toast({ title: "Choose files to upload", status: "info" });
      return;
    }
    setProcessing(true);
    try {
      const form = new FormData();
      if (title.trim()) form.append("title", title.trim());
      pendingFiles.forEach((f) => form.append("files", f, f.name));
      const res = await hitBeApiFormData("dashboard/media-analyze", form);
      const job = res?.data?.job;
      applyCredits(res?.data?.userTokenDetails);
      clearPending();
      setTitle("");
      if (job?._id) {
        setActiveJobId(job._id);
        setActiveJob(job);
      }
      loadJobs();
      if (res?.data?.partial) {
        toast({
          title: "Partial analysis saved",
          description:
            res?.data?.job?.errorMessage ||
            "Some files could not be analyzed. Review completed items below.",
          status: "warning",
          duration: 7000,
        });
      } else {
        toast({ title: "Analysis complete", status: "success", duration: 3000 });
      }
    } catch (e) {
      const msg = String(e?.message || "").toLowerCase();
      if (msg.includes("credit") || msg.includes("token")) {
        promptBuyCredits();
      }
      toast({
        title: "Analysis failed",
        description: e.message,
        status: "error",
        duration: 7000,
      });
      await loadJobs();
    } finally {
      setProcessing(false);
    }
  };

  const deleteJob = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this analysis from history?")) return;
    try {
      await hitBeApi(`dashboard/media-analyze/jobs/${id}`, "DELETE");
      if (activeJobId === id) {
        setActiveJobId(null);
        setActiveJob(null);
      }
      loadJobs();
      toast({ title: "Deleted", status: "info", duration: 2000 });
    } catch (err) {
      toast({ title: "Delete failed", description: err.message, status: "error" });
    }
  };

  const previewUrls = useMemo(() => {
    return pendingFiles.map((f) => {
      const kind = filePreviewKind(f);
      return {
        file: f,
        kind,
        url: kind === "image" || kind === "video" ? URL.createObjectURL(f) : null,
      };
    });
  }, [pendingFiles]);

  useEffect(() => {
    return () => {
      previewUrls.forEach((p) => revokeBlobUrl(p.url));
    };
  }, [previewUrls]);

  const showUploadPanel = !activeJobId;
  const mediaGuide = DASHBOARD_TAB_GUIDES["media-analyze"];

  const mediaSteps = [
    { label: "Upload", hint: "Images, a folder, or one or more videos." },
    { label: "AI reads content", hint: "Each image or ~30s video segment is explained." },
    { label: "Review history", hint: "Reopen past analyses anytime from the sidebar." },
  ];

  return (
    <Box bg={pageBg} minH="100%" py={{ base: 3, md: 5 }} px={{ base: 2, md: 4 }}>
      <Box maxW="1280px" mx="auto">
        <Flex direction={{ base: "column", lg: "row" }} gap={5} align="stretch">
          <AiHistorySidebar
            theme="purple"
            newLabel="New upload"
            isNewActive={showUploadPanel}
            onNew={() => {
              setActiveJobId(null);
              setActiveJob(null);
            }}
            onRefresh={loadJobs}
            loading={jobsLoading}
            emptyText="No analyses yet. Upload images or a video to start."
          >
            {jobs.map((j) => (
              <AiHistoryItem
                key={j._id}
                theme="purple"
                active={activeJobId === j._id}
                title={j.title}
                meta={
                  <HStack spacing={1} mt={1} flexWrap="wrap">
                    <Badge size="sm" colorScheme={accent} variant="subtle">
                      {sourceTypeLabel(j.sourceType)}
                    </Badge>
                    {j.status === "failed" ? (
                      <Badge size="sm" colorScheme="red">
                        Failed
                      </Badge>
                    ) : null}
                    <Text fontSize="xs" color={muted}>
                      {j.itemCount} item{j.itemCount === 1 ? "" : "s"}
                    </Text>
                  </HStack>
                }
                preview={formatJobDate(j.createdAt)}
                onSelect={() => setActiveJobId(j._id)}
                onDelete={(e) => deleteJob(j._id, e)}
              />
            ))}
          </AiHistorySidebar>

          <Box flex="1" minW={0}>
            <VStack align="stretch" spacing={5}>
              {showUploadPanel ? (
                <Box position="relative">
                  <AiCreditsPaywall
                    show={!canAnalyze}
                    featureName="Media AI"
                    onBuy={promptBuyCredits}
                    theme="purple"
                  />
                  {mediaGuide ? (
                    <AiFeatureGuidePanel
                      theme="purple"
                      title={mediaGuide.title}
                      intro={mediaGuide.intro}
                      helps={mediaGuide.helps}
                      showWorkspaceLink={false}
                    >
                      <AiCreditsBanner
                        canUse={canAnalyze}
                        remaining={aiCreditsRemaining}
                        onBuy={promptBuyCredits}
                        variant="light"
                      />
                    </AiFeatureGuidePanel>
                  ) : null}

                  <Box
                    bg={cardBg}
                    borderRadius="2xl"
                    borderWidth="1px"
                    borderColor={borderColor}
                    p={{ base: 4, md: 6 }}
                    boxShadow="md"
                  >
                  <AiStepStrip steps={mediaSteps} theme="purple" />

                  <FormControl mb={4} maxW="md">
                    <FormLabel fontSize="sm">Title (optional)</FormLabel>
                    <Input
                      placeholder="e.g. Geography chapter 3 slides"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      isDisabled={processing}
                    />
                  </FormControl>

                  <Box
                    borderWidth="2px"
                    borderStyle="dashed"
                    borderColor={dropBorder}
                    borderRadius="xl"
                    bg={dropBg}
                    p={6}
                    textAlign="center"
                    mb={4}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      addFiles(e.dataTransfer.files);
                    }}
                  >
                    <Icon as={FiUpload} boxSize={8} color="purple.500" mb={2} />
                    <Text fontSize="sm" fontWeight="medium" mb={3}>
                      Drag & drop images or video here
                    </Text>
                    <HStack justify="center" flexWrap="wrap" gap={2}>
                      <Button
                        size="sm"
                        leftIcon={<FiImage />}
                        onClick={() => fileInputRef.current?.click()}
                        isDisabled={processing}
                      >
                        Images / video
                      </Button>
                      <Button
                        size="sm"
                        leftIcon={<FiFolder />}
                        onClick={() => folderInputRef.current?.click()}
                        isDisabled={processing}
                      >
                        Folder
                      </Button>
                    </HStack>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept={ACCEPT_FILES}
                      hidden
                      onChange={(e) => {
                        addFiles(e.target.files);
                        e.target.value = "";
                      }}
                    />
                    <input
                      ref={folderInputRef}
                      type="file"
                      multiple
                      webkitdirectory=""
                      directory=""
                      hidden
                      onChange={(e) => {
                        addFiles(e.target.files);
                        e.target.value = "";
                      }}
                    />
                    <Text fontSize="xs" color={muted} mt={3}>
                      Images: JPEG, PNG, WebP, GIF. Video: MP4, WebM, MOV.
                    </Text>
                  </Box>

                  {pendingFiles.length > 0 ? (
                    <Box mb={4}>
                      <HStack justify="space-between" mb={2}>
                        <Text fontSize="sm" fontWeight="semibold">
                          Selected ({pendingFiles.length})
                        </Text>
                        <Button size="xs" variant="ghost" onClick={clearPending}>
                          Clear all
                        </Button>
                      </HStack>
                      <SimpleGrid columns={{ base: 2, sm: 4, md: 5 }} spacing={2}>
                        {previewUrls.map((p, i) => (
                          <Box
                            key={`${p.file.name}-${i}`}
                            borderRadius="md"
                            overflow="hidden"
                            borderWidth="1px"
                            borderColor={borderColor}
                            position="relative"
                          >
                            {p.kind === "image" && p.url ? (
                              <Image
                                src={p.url}
                                alt={p.file.name}
                                h="72px"
                                w="100%"
                                objectFit="cover"
                              />
                            ) : p.kind === "video" && p.url ? (
                              <Box
                                as="video"
                                src={p.url}
                                h="72px"
                                w="100%"
                                objectFit="cover"
                                muted
                                playsInline
                                preload="metadata"
                              />
                            ) : (
                              <Flex
                                h="72px"
                                align="center"
                                justify="center"
                                bg={segmentBg}
                              >
                                <Icon as={FiFilm} color="purple.400" boxSize={6} />
                              </Flex>
                            )}
                            <Text fontSize="2xs" p={1} noOfLines={1} title={p.file.name}>
                              {p.file.name}
                            </Text>
                            <IconButton
                              size="xs"
                              position="absolute"
                              top={1}
                              right={1}
                              aria-label="Remove"
                              icon={<FiTrash2 />}
                              onClick={() => removePending(i)}
                            />
                          </Box>
                        ))}
                      </SimpleGrid>
                    </Box>
                  ) : null}

                  <Button
                    colorScheme="purple"
                    size="lg"
                    w={{ base: "full", sm: "auto" }}
                    leftIcon={
                      processing ? <Spinner size="sm" /> : <FiUpload />
                    }
                    onClick={runAnalysis}
                    isLoading={processing}
                    loadingText="Analyzing with AI…"
                    isDisabled={!pendingFiles.length || !canAnalyze}
                  >
                    Analyze with AI
                  </Button>
                  </Box>
                </Box>
              ) : (
                <Box
                  bg={cardBg}
                  borderRadius="2xl"
                  borderWidth="1px"
                  borderColor={borderColor}
                  p={{ base: 4, md: 6 }}
                  boxShadow="sm"
                >
                  <HStack justify="space-between" mb={4} flexWrap="wrap">
                    <Box>
                      <Heading size="md">{activeJob?.title || "Analysis"}</Heading>
                      <HStack mt={1} spacing={2} color={muted} fontSize="sm">
                        <Icon as={FiClock} boxSize={3} />
                        <Text>{formatJobDate(activeJob?.createdAt)}</Text>
                        <Badge colorScheme="purple">
                          {sourceTypeLabel(activeJob?.sourceType)}
                        </Badge>
                      </HStack>
                    </Box>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setActiveJobId(null);
                        setActiveJob(null);
                      }}
                    >
                      New upload
                    </Button>
                  </HStack>

                  {activeJob?.status === "failed" ? (
                    <Alert status="error" borderRadius="xl" mb={4}>
                      <AlertIcon />
                      <Box>
                        <AlertTitle fontSize="sm">Analysis failed</AlertTitle>
                        <AlertDescription fontSize="sm">
                          {activeJob.errorMessage || "Something went wrong. Try a new upload."}
                        </AlertDescription>
                      </Box>
                    </Alert>
                  ) : null}

                  {!activeJob?.items?.length && activeJob?.status !== "failed" ? (
                    <Flex justify="center" py={10}>
                      <Spinner color="purple.500" />
                    </Flex>
                  ) : null}

                  <MediaAnalyzeVideoSources
                    sources={activeJob?.mediaSources || []}
                  />

                  <VStack align="stretch" spacing={4}>
                    {(activeJob?.items || []).map((item, idx) => (
                      <MediaAnalyzeItemCard
                        key={item._id || idx}
                        item={item}
                        accent={accent}
                        segmentBg={segmentBg}
                        borderColor={borderColor}
                      />
                    ))}
                  </VStack>
                </Box>
              )}
            </VStack>
          </Box>
        </Flex>
      </Box>

      <VoiceCreditPaymentModal
        isOpen={voicePayOpen}
        onClose={() => setVoicePayOpen(false)}
        variant={canAnalyze ? "topUp" : "exhausted"}
      />
    </Box>
  );
}
